import { useMemo } from "react";
import type { TFunction } from "i18next";
import type { TimeReportsV2Group } from "services/api/company/timeReports.service";
import {
  createPersonDetailRowViewModel,
  createPersonDetailSelection,
  createPersonSummaryViewModel,
  createUpdatedAtLabel,
} from "../components/row/personRow.mapper";
import type { PersonDetailSelection } from "../models/personView.types";

interface UsePersonRowViewModelParams {
  row: TimeReportsV2Group;
  deviationToleranceMinutes: number;
  includeFlexTimeDeviations: boolean;
  selectedDetailIds: number[];
  locale: string;
  translate: TFunction;
}

export interface PersonRowDetailEntry {
  detailId: number;
  selection: PersonDetailSelection;
  updatedAtLabel: string;
}

export const usePersonRowViewModel = (
  params: UsePersonRowViewModelParams,
): {
  teamLabel: string | null;
  summary: ReturnType<typeof createPersonSummaryViewModel>;
  detailEntries: PersonRowDetailEntry[];
  rowSelections: PersonDetailSelection[];
  selectionByDetailId: Map<number, PersonDetailSelection>;
  selectedDetailIdSet: Set<number>;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
} => {
  const selectedDetailIdSet = useMemo(
    () => new Set(params.selectedDetailIds),
    [params.selectedDetailIds],
  );

  const summary = useMemo(
    () => createPersonSummaryViewModel(params.row),
    [params.row],
  );

  const detailEntries = useMemo(() => {
    return params.row.items.map((item) => {
      const detail = createPersonDetailRowViewModel(
        item,
        params.locale,
        params.deviationToleranceMinutes,
        params.includeFlexTimeDeviations,
        params.translate,
      );
      const selection = createPersonDetailSelection(detail, item, params.row);

      return {
        detailId: detail.id,
        selection,
        updatedAtLabel: createUpdatedAtLabel(
          item.updatedAt,
          params.locale,
          params.translate,
        ),
      };
    });
  }, [
    params.deviationToleranceMinutes,
    params.includeFlexTimeDeviations,
    params.locale,
    params.row,
    params.translate,
  ]);

  const rowSelections = useMemo(
    () => detailEntries.map((entry) => entry.selection),
    [detailEntries],
  );
  const selectionByDetailId = useMemo(
    () =>
      new Map(
        rowSelections.map((selection) => [selection.detail.id, selection]),
      ),
    [rowSelections],
  );

  const isAllSelected =
    detailEntries.length > 0 &&
    detailEntries.every((entry) => selectedDetailIdSet.has(entry.detailId));

  const isPartiallySelected =
    !isAllSelected &&
    detailEntries.some((entry) => selectedDetailIdSet.has(entry.detailId));

  const teamLabel = useMemo((): string | null => {
    const uniqueTeamNames = new Set(
      params.row.items
        .map((item) => (item.workGroupName ?? "").trim())
        .filter((name) => name.length > 0),
    );
    if (uniqueTeamNames.size !== 1) return null;
    return uniqueTeamNames.values().next().value ?? null;
  }, [params.row.items]);

  return {
    teamLabel,
    summary,
    detailEntries,
    rowSelections,
    selectionByDetailId,
    selectedDetailIdSet,
    isAllSelected,
    isPartiallySelected,
  };
};
